/**
 * Put a verified email on a signed-in account.
 *
 * Two calls, in this order: `send` mails a one-time code, `confirm` checks it
 * and writes the address to the USERS row. They were written inline in
 * `components/login/useLoginAuth.ts` for the login modal's more-info step;
 * checkout now needs the same pair, and a second copy of a flow that mutates an
 * account's identity is not something to keep two of.
 *
 * Phone-only signups are 27.7% of registrations and land with `email = null`,
 * so this is the only path by which most of those accounts ever get one.
 */
import { trackError, ErrorSeverity } from './error-tracking';
import { getRecaptchaToken } from './recaptcha-token';
import { postWithBotChallenge } from './bot-challenge-client';

/**
 * Is this a mailable address?
 *
 * Defined here rather than imported from `checkout-validation`, which pulls in
 * `components/utils` and, through it, the whole `shared.post` copy tree — too
 * much weight to drag onto the account page for one regex, and it broke that
 * page's suite the moment it was imported. `checkout-validation` re-exports
 * this one, so there is still a single definition.
 *
 * @param email - Address to check
 * @returns True when it can be mailed to
 */
export function isValidEmail(email: string): boolean {
  if (!email) return false;

  // Basic email regex - matches the logic in checkEmailValidity
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * What a step returns.
 *
 * `error` is a stable KEY the caller maps to its own wording — never the
 * server's sentence. Passing `data.error` straight through looked harmless and
 * was not: the verify endpoint answers a wrong code with "Invalid or expired
 * verification code", which matched no key, so the UI fell through to its
 * default and told people "We couldn't send the code" after they mistyped one.
 * The server's own words are kept in `detail`, for logs.
 */
export type EmailVerificationError = 'send_failed' | 'invalid_code' | 'too_many_attempts' | 'network_error';

export interface EmailVerificationResult {
  success: boolean;
  error?: EmailVerificationError;
  /** The server's reason, for diagnostics. Never rendered. */
  detail?: string;
}

export interface SendEmailCodeInput {
  email: string;
  /** The route refuses a send with no token. Omit it and one is minted here. */
  recaptchaToken?: string;
  /** The login funnel's session, so this path's otp_sent events are not orphans. */
  funnelSessionId?: string;
}

export interface ConfirmEmailCodeInput {
  email: string;
  code: string;
  firstName?: string;
  lastName?: string;
}

/** Mail a one-time code to `email`. */
export async function sendEmailCode(input: SendEmailCodeInput): Promise<EmailVerificationResult> {
  try {
    const response = await postWithBotChallenge('/api/auth/send-code', {
      emailOrPhone: input.email,
      recaptchaToken: input.recaptchaToken ?? await getRecaptchaToken('email_verification'),
      funnelSessionId: input.funnelSessionId,
      // A signed-in member adding an email — a code-only challenge, kept
      // distinct from a login so a login code cannot satisfy this redeem.
      purpose: 'email_capture',
    });

    const data = await response.json();
    if (response.ok && data.success) return { success: true };
    return { success: false, error: 'send_failed', detail: data.error };
  } catch (e) {
    trackError('Failed to send an email verification code', ErrorSeverity.WARNING, {
      component: 'email-verification',
      action: 'sendEmailCode',
      extra: { error: e instanceof Error ? e.message : String(e) },
    });
    return { success: false, error: 'network_error' };
  }
}

/**
 * Check the code and save the address. A wrong or expired code is the user's
 * typo, not a fault of ours — it comes back as `invalid_code` for the caller to
 * show, and is never logged as an error.
 */
export async function confirmEmailCode(
  input: ConfirmEmailCodeInput
): Promise<EmailVerificationResult> {
  try {
    const response = await fetch('/api/user/verify-email', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: input.email,
        code: input.code,
        ...(input.firstName ? { firstName: input.firstName } : {}),
        ...(input.lastName ? { lastName: input.lastName } : {}),
      }),
    });

    const data = await response.json();
    if (response.ok && data.success) return { success: true };
    if (data.errorCode === 'TOO_MANY_ATTEMPTS') {
      return { success: false, error: 'too_many_attempts', detail: data.error };
    }
    return { success: false, error: 'invalid_code', detail: data.error };
  } catch (e) {
    trackError('Failed to confirm an email verification code', ErrorSeverity.WARNING, {
      component: 'email-verification',
      action: 'confirmEmailCode',
      extra: { error: e instanceof Error ? e.message : String(e) },
    });
    return { success: false, error: 'network_error' };
  }
}

/**
 * Does this signed-in member still owe us an email address?
 *
 * A guest is already asked for one; a signed-in buyer never was, and a phone-only
 * signup (27.7% of registrations, `email = null` on the USERS row) reaches Pay
 * with nothing to send a receipt to. The transaction mail contract is
 * `buyerEmail: z.string().email()`, so the order completes and the receipt cannot
 * be built at all.
 *
 * An address that fails `isValidEmail` counts as missing: a row carrying a
 * placeholder is no more mailable than a null one.
 *
 * @param loggedIn - Whether a session is established
 * @param profile - The signed-in user's profile, if it has loaded
 * @returns True when checkout must collect and verify an email first
 */
export function needsEmailCapture(
  loggedIn: boolean,
  profile: { email?: string | null } | null | undefined
): boolean {
  // Not signed in: the guest email field already covers this, and a profile that
  // has not loaded yet must not flash the prompt at someone who has an email.
  if (!loggedIn || !profile) return false;

  return !isValidEmail(profile.email ?? '');
}
